import { otherSeriesColor, seriesPalette } from "@intentsify/brand";
import { colorLegend, defineChart } from "@tanstack/charts";
import { pie, polar, radialArc } from "@tanstack/charts/polar";
import { scaleOrdinal } from "@tanstack/charts/scales/ordinal";
import { BrandChartTheme } from "../BrandChart";
import { PieChartItem } from "./PieChart.types";

/** Gap between slices, in radians. */
const SLICE_GAP = 0.02;
const DONUT_HOLE = 0.62;
const OTHER_LABEL = /^other$/i;

export type DonutSlice = PieChartItem & { color: string };

const byValueDescending = (a: PieChartItem, b: PieChartItem) =>
  b.value - a.value;

/**
 * Orders items and assigns each its colour once, so the donut, legend and
 * export always agree. Explicit `colors` belong to the items in their given
 * order; brand colours go largest-first, and catch-all "Other" rows use the
 * neutral series colour.
 */
export const getDonutSlices = (
  data: PieChartItem[],
  brandTheme: BrandChartTheme,
  { shouldSort, colors }: { shouldSort: boolean; colors?: string[] },
): DonutSlice[] => {
  if (colors) {
    const coloured = data.map((item, index) => ({
      ...item,
      color: colors[index % colors.length],
    }));

    return shouldSort ? coloured.sort(byValueDescending) : coloured;
  }

  const ordered = shouldSort ? [...data].sort(byValueDescending) : data;
  const palette = seriesPalette[brandTheme.mode];
  let paletteIndex = 0;

  return ordered.map((item) => ({
    ...item,
    color: OTHER_LABEL.test(item.label)
      ? otherSeriesColor[brandTheme.mode]
      : palette[paletteIndex++ % palette.length],
  }));
};

export const buildDonutDefinition = (
  slices: DonutSlice[],
  {
    brandTheme,
    hiddenLabels = [],
    withLegend = false,
  }: {
    brandTheme: BrandChartTheme;
    hiddenLabels?: string[];
    /** Draws the legend into the scene; used for PNG export. */
    withLegend?: boolean;
  },
) => {
  const visible = slices.map((slice) =>
    hiddenLabels.includes(slice.label) ? { ...slice, value: 0 } : slice,
  );

  return defineChart({
    theme: brandTheme.theme,
    guides: false,
    scales: { x: null, y: null },
    color: {
      scale: scaleOrdinal<string, string>()
        .domain(slices.map((slice) => slice.label))
        .range(slices.map((slice) => slice.color)),
      legend: withLegend ? colorLegend() : undefined,
    },
    marks: [
      polar({
        inset: 2,
        scales: { angle: null, radius: null },
        marks: [
          radialArc(pie(visible, { value: "value", gapAngle: SLICE_GAP }), {
            key: "label",
            color: "label",
            innerRadius: ({ radius }) => radius * DONUT_HOLE,
            cornerRadius: 2,
          }),
        ],
      }),
    ],
  });
};
